import { rux } from './../../../lib/rux.js'
import router from './../../router'
import Vue from 'vue'
import { fetchReferralLinkFromEmail } from "../../../lib/klaviyo.js"
const RuxStore = {
  namespaced: true,
  state: {
    // The user profile
    userProfile: {},
    // The referral link, if any
    referralLink: {},
    // The list of ruxes for the user
    ruxList: [],
    // The list of all the images stored
    imageList: [],
    // The current rux
    currentRux: [],
    // The images of the current rux
    currentRuxImages: [],
    // Whether or not the search bar is visible. As there are triggers for search in multiple places it makes sense to store here
    showSearchBar: false,
    // Geolocation values
    geolocation: {
      latitude: null,
      longitude: null,
      isSet: false
    },
    register: {
      serialNumber: '',
      forcePin: true
    },

    showLoading: false,
    // Global error array. This is displayed as toast in the ErrorToast component
    errors: [],
    successes: []

  },
  actions: {
    /**
       * @summary Gets all the RUX. This is called as soon as the app fires up
       * @commits SETRUXLIST
       */
    async getAllRux ({ dispatch, commit }) {
      if (rux.isAuth()) {
        try {
          // Get the data without images for speedy loading
          const ruxList = await rux.getRuxFromDB()
          // Commit to store
          commit('SETRUXLIST', ruxList)
        } catch (error) {
          // Use the global error handle to handle the error
          dispatch('error', error, { root: true })
        }
      }
    },

    /**
       * @summary Gets a single rux from the database based on the serial number
       * @param {string} serialNumber - The serial number of the rux to get
       * @commits SETCURRENTRUX
       */
    async getRux ({ dispatch, commit }, serialNumber) {
      try {
        // Get the rux from the db and commit it to the store
        const ruxx = await rux.getSingleRuxFromDB(serialNumber)
        if (ruxx.length === 0) {
          dispatch('error', { message: 'No RUX with that Serial Number exists in your account' }, { root: true })
        } else {
          commit('SETCURRENTRUX', ruxx)
        }
      } catch (error) {
        dispatch('error', error, { root: true })
      }
    },
    async checkRUXExistsInGlobalList ({ dispatch, commit }, serialNumber) {
      try {
        return await rux.checkRuxExistsInGlobalList(serialNumber)
      } catch (error) {
        dispatch('error', error, { root: true })
      }
    },
    /**
       * @summary Add a rux to the database. If you add fields to the rux, be sure to update the Joi schema in lib/rux.js
       * @param {object} form // See rux.validate() for schema definition.
       */
    async addRux ({ dispatch, state }, form) {
      try {
        if (state.geolocation && state.geolocation.latitude && state.geolocation.longitude) {
          form.location = state.geolocation
        }
        // Add the Rux to the DB.
        await rux.addRux(form)
        await rux.addRuxToGlobalList(form.serialNumber)
        dispatch('checkReferralLink', form.serialNumber)
        dispatch('getAllRux')
        this.commit('SUCCESS', 'RUX Added successfully')
        router.push({ name: 'List' })
      } catch (error) {
        // Use the global error handle to handle the error
        dispatch('error', error, { root: true })
      }
    },
    /**
       * @summary - Updates a rux in the database. If you add fields to the rux, be sure to update the Joi schema in lib/rux.js
       * @param {object} form // See rux.validate() for schema definition.
       */
    async updateRux ({ dispatch, state }, form) {
      try {
        if (form.updateLocation && state.geolocation && state.geolocation.latitude && state.geolocation.longitude) {
          form.location = state.geolocation
        }
        // Update the rux here
        await rux.updateRux(form)
        // Reload the list of all RUX and this indvidual one too. Forces UI update everywhere
        dispatch('getRux', form.serialNumber)
        dispatch('getAllRux')
      } catch (error) {
        // Use the global error handle to handle the error
        dispatch('error', error, { root: true })
      }
    },
    /**
       * @summary Deletes a rux from a user's account and the global list
       * @param {string} serialNumber - The serial number to delete
       * @param {string} route - The route to push to after the delete
       */
        async deleteRuxFromAccount ({ dispatch, commit }, { serialNumber, route }) {
          try {
            // Delete the rux from both the users list and the global list
            await rux.deleteSingleRuxFromDB(serialNumber)
            await rux.deleteRuxFromGlobalList(serialNumber)
            dispatch('getAllRux')
            router.push(route)
          } catch (error) {
            dispatch('error', error, { root: true })
          }
        },
    /**
       * @summary - Updates a profile in the database. If you add fields to the profile, be sure to update the Joi schema in lib/rux.js
       * @param {object} user - The user object to update
       */
    async updateProfile ({ dispatch, commit }, user) {
      try {
        await rux.createAndUpdateUserProfile(user)
        commit('SETUSERPROFILE', user)
      } catch (error) {
        dispatch('error', error, { root: true })
      }
    },
    /**
       * @summary - Upload an image to the database and update the UI
       * @param {object} imageDetails - contains all the image details
       * @param {File} imageDetails.image - The image to upload
       * @param {string} imagedetails.serialNumber - the Serial Number of the RUX that this image is assocaited with
       * @param {boolean} imageDetails.replace - Whether to replace the rux or not. This is essential to not adding the image to the UI twice
       * @commit ADDIMAGETORUX
       */
    async uploadImage ({ dispatch, commit }, imageDetails) {
      // Unpack the object that was passed in
      const { replace, serialNumber } = imageDetails
      try {
        // Upload the image here and get the ref back
        const image = await rux.uploadImage(imageDetails)
        // Get the URL of the image here. This is a built in firebase function. Maybe should be in rux
        const imagePath = await image.ref.getDownloadURL()
        // Commit to the store which forces a UI Update. Replace set to true finds the image with the same name, other wise push
        commit('ADDIMAGETORUX', { imagePath, imageName: imageDetails.image.name, replace })
        // Update the rux here to add the last updated time here.
        dispatch('updateRux', { serialNumber })
        // Show a nice green bar to let people know it all worked.
        commit('SUCCESS', 'Image uploaded successfully')
      } catch (error) {
        // Dispatch to the global error handler here.
        dispatch('error', error, { root: true })
      }
    },
    /**
       * @summary Delete an image from the associated RUX
       * @param {Object} imageDetails - An object that contains all options necessary to delete the image
       * @param {number} imageDetails.index - The index of the image to delete. Used for the UI
       * @param {string} imageDetails.serialNumber - The serial number which this image belongs to
       * @param {string} imageDetails.imageName - This is the name of the file including the extension. Not the URL!
       * @commit DELETEIMAGEFROMRUX
       */
    async deleteImage ({ dispatch, commit }, imageDetails) {
      try {
        // Try and delete the image from the db here
        await rux.deleteImage(imageDetails)
        // Update the UI here
        commit('DELETEIMAGEFROMRUX', { index: imageDetails.index, serialNumber: imageDetails.serialNumber })
        // Update the rux here. This forces an update to the last updated time field
        dispatch('updateRux', { serialNumber: imageDetails.serialNumber })
      } catch (error) {
        dispatch('error', error, { root: true })
      }
    },
    /**
       * @summary Get the images associated with this Serial Number
       * @param {string} serialNumber the serialNumber to get the images for
       * @commit SETCURRENTIMAGES
       */
    async getImages ({ dispatch, commit }, serialNumber) {
      try {
        // Get the images for this particular serial Number
        const images = await rux.getImages(serialNumber)
        // Commit it the UI
        commit('SETCURRENTIMAGES', images)
      } catch (error) {
        dispatch('error', error, { root: true })
      }
    },
    /**
       * @summary Gets all the image URL's for all the RUX.
       * @commit SETALLIMAGES
       */
    async getAllImages ({ dispatch, state, commit }) {
      // Start getting all the images for all the rux here.
      const promises = state.ruxList.map((item) => {
        return rux.getImages(item.serialNumber)
      })
      // Wait for all the images to be done
      Promise.all(promises).then((results) => {
        // This sets all images as an array of objects. The object has the the structure {serialNumber:serialNumber, images: []}
        commit('SETALLIMAGES', results.map((images, index) => {
          return {
            serialNumber: state.ruxList[index].serialNumber,
            images
          }
        }))
      })
    },
    /**
       * @summary Adds a packing list item to the associated
       * @param {object} params
       * @param {string} params.item - The item that needs to get added
       * @param {string} params.serialNumber - The serial number to update.
       * @commit ADDPACKINGLISTITEM
       */
    async addPackingListItem ({ dispatch, commit }, params) {
      // Get the item to add from the params
      const { item, serialNumber } = params
      try {
        // Add the item and get the firebase ref.
        await rux.addPackingListItem(params)
        // Update the UI.
        commit('ADDPACKINGLISTITEM', { item, serialNumber })
        dispatch('updateRux', { serialNumber })
      } catch (error) {
        dispatch('error', error, { root: true })
      }
    },
    async updatePackingListItem ({ dispatch, commit, state }, params) {
      const { index, value } = params
      const item = {
        serialNumber: state.currentRux.serialNumber,
        item: { key: state.currentRux.packingListItems[index].key, value }
      }
      try {
        // Update the DB here
        await rux.updatePackingListItem(item)
        commit('UPDATEPACKINGLIST', params)
      } catch (error) {
        dispatch('error', error, { root: true })
      }
    },
    /**
       * @summary Deletes a packing list item
       * @param {Object} params
       * @param {number} params.index - The index to delete the item from
       * @param {string} params.serialNumber - The serial number to delete from
       * @param {string} params.item - the item to delete from
       * @commit DELETEPACKINGLISTITEM
       */
    async deletePackingListItem ({ dispatch, commit }, params) {
      // Get the index here from the params
      const { index } = params
      try {
        // Delete from the DB here
        await rux.deletePackingListItem(params)
        // Delete from the UI here.
        commit('DELETEPACKINGLISTITEM', index)
        dispatch('updateRux', { serialNumber: params.serialNumber })
      } catch (error) {
        dispatch('error', error, { root: true })
      }
    },
    /**
       * @summary Deletes all packing list items
       * @param {string} serialNumber - The serial number to delete from
       * @commit DELETEPACKINGLISTITEMS
       */
    async deleteAllPackingListItems ({ dispatch, commit }, serialNumber) {
      // Get the index here from the params
      try {
        // Delete from the DB here
        await rux.deleteAllPackingListItems(serialNumber)
        // Delete from the UI here.
        commit('DELETEALLPACKINGLISTITEMS')
        dispatch('updateRux', { serialNumber: serialNumber })
      } catch (error) {
        dispatch('error', error, { root: true })
      }
    },
    async checkReferralLink ({ dispatch, commit }, serialNumber) {
       try {
        const globalRuxReferralLink = await rux.checkRuxHasReferralLink(serialNumber)
        if (globalRuxReferralLink) {
          commit('SETREFERRALLINK', globalRuxReferralLink)
          return globalRuxReferralLink
       }
        const email = await rux.checkRuxRegisteredToEmail(serialNumber)
        const referralLinkKlaviyo = await fetchReferralLinkFromEmail(email)
        if (referralLinkKlaviyo) {
          commit('SETREFERRALLINK', referralLinkKlaviyo)
          await rux.updateReferralLinkRuxGlobalList(serialNumber, referralLinkKlaviyo)
          return referralLinkKlaviyo
        }
      } catch (error) {
        dispatch('error', error, { root: true })
      }
    },
    /**
       * Show or hide the search bar
       * @param {Boolean} value - true to show false to hide.
       */
    showSearchBar ({ commit, state }, value) {
      commit('SEARCHBAR', value)
    },
    /**
       * Set the Geolocation coords
       * @param {Object} value
       * @param {String} value.lat - The Geolocation latitude
       * @param {String} value.long - The Geo location longitude
       * @param {Boolean} value.isSet - A boolean to let you know if the Coords have been set or not.
       */
    setGeolocation ({ commit }, value) {
      commit('SETGEOLOCATION', value)
    },
    /**
       * Whether to make the end user enter a pin to register the RUX
       * @param {Boolean} value - True to force, false to ignore PIN
       */
    setForcePinOnRegistration ({ commit }, value) {
      commit('SETFORCEPINONREGISTRATION', value)
    },
    /**
       * Used in the registration process by the route, so that we don't need to show the serial number at all
       * @param {string} value - The serial number to add to the store
       */
    setSerialNumberForRegistration ({ commit }, value) {
      commit('SETSERIALNUMBERFORREGISTRATION', value)
    },
    /**
       * Checks whether an encrypted serial number is valid or not. Used on the scan route
       * @param {String} value  - A hashed serial number.
       * @returns {Promise} - Returns a promise with the details of the RUX.
       */
    async checkEncryptedSerialNumberIsValid ({ commit, dispatch }, value) {
      // Show the loading bar
      commit('SETSHOWLOADING', true)
      try {
        // This calls a firebase function to check whether is valid
        const decryptedSerial = await rux.validateSerialNumber(value)
        if (decryptedSerial) {
          // Commits the serial number to the store.
          dispatch('setSerialNumberForRegistration', decryptedSerial.data.serialNumber)
          // Actually return the value so it can be used.
          return decryptedSerial
        }
      } catch (error) {
        // Throw a fairly generic error, not allowing the actual error to bubble to the client
        dispatch('error', { message: 'Unknown Serial Number' }, { root: true })
      } finally {
        // Hide the loading
        commit('SETSHOWLOADING', false)
      }
    },
    /**
       * Validates a pin code against a serial number
       * @param {*} value
       */
    async validatePinCode ({ commit, state }, value) {
      const { pin } = value
      // Show a loading screen
      commit('SETSHOWLOADING', true)
      try {
        // Calls a firebase function to confirm the passed pin vs what is in the store
        const result = await rux.confirmPin(pin, state.register.serialNumber)
        // Actually return the result
        return result
      } finally {
        // Stop the show loading spinner
        commit('SETSHOWLOADING', false)
      }
    },
    /**
     * Clears the current rux and its images
     */
    clearCurrentRUX ({ commit }) {
      commit('CLEARCURRENTRUX')
    },
    setShowLoading: {
      root: true,
      handler ({ commit }, val) {
        commit('SETSHOWLOADING', val)
      }
    },
    /**
       * @summary Acts as a global error handler. Calling this will show an error toast as well as log to the console
       * @param {string} errorMessage The error message you want displayed
       * @commit ERROR
       */
    error: {
      root: true,
      handler ({ dispatch, commit }, errorMessage) {
        console.log(errorMessage)
        commit('ERROR', errorMessage)
      }
    }
  },
  mutations: {
    // Set the user profile here
    SETUSERPROFILE (state, val) {
      state.userProfile = val
    },
    // Set referral link here
    SETREFERRALLINK (state, val) {
      state.referralLink = val
    },
    // Set the rux list here
    SETRUXLIST (state, val) {
      state.ruxList = val
    },
    // Set the current rux images.
    SETCURRENTIMAGES (state, val) {
      state.currentRuxImages = val
    },
    // Set all the images for all RUX
    SETALLIMAGES (state, val) {
      state.imageList = val
    },
    // This adds an images property the ruxlist for each rux in the list if it exists
    ADDIMAGESTORUX (state, val) {
      const { images, serialNumber } = val
      // Find the rux
      const rux = state.ruxList.find((rux) => {
        return rux.serialNumber === serialNumber
      })
      // Removes the images already there, this causes Vue to update
      rux.images = rux.images.slice()
      // Add them back in.
      images.forEach((image) => {
        rux.images.push(image)
      })
    },
    // Adds an image to the specified RUX
    ADDIMAGETORUX (state, val) {
      // Unpack the values
      const { imageName, imagePath, replace } = val
      // If the client decides to replace, find it in the list and replace.
      if (replace) {
        const matchedImageIndex = state.currentRuxImages.findIndex((img) => {
          return imageName === img.match('%2..*%2F(.*?)?alt')[1].slice(0, -1)
        })
        if (matchedImageIndex) {
          state.currentRuxImages.splice(matchedImageIndex, 1)
        }
      }
      // Other wise just add it on the end
      state.currentRuxImages.push(imagePath)
    },
    // Delets an image from the rux
    DELETEIMAGEFROMRUX (state, val) {
      const { index } = val
      state.currentRuxImages.splice(index, 1)
    },
    // Sets the current rux
    SETCURRENTRUX (state, val) {
      state.currentRux = val[0]
    },
    // Add the packing list item here
    ADDPACKINGLISTITEM (state, val) {
      // Add the packing list item to the current rux and also the full list
      const { item, serialNumber } = val
      if (!Array.isArray(state.currentRux.packingListItems)) {
        // Add immediate reactivity by using Vue.set
        Vue.set(state.currentRux, 'packingListItems', [])
      }
      state.currentRux.packingListItems.push(item)

      // Add to the full list of RUX here
      // Find the rux in the list
      const rux = state.ruxList.find((rux) => {
        return rux.serialNumber === serialNumber
      })
      // Make sure its an array and if not create an array
      if (!Array.isArray(rux.packingListItems)) {
        // Add immediate reactivity by using Vue.set
        Vue.set(rux, 'packingListItems', [])
      }
      // Push the packing list item on here
      rux.packingListItems.push(item)
    },
    // Delete a packing list item
    DELETEPACKINGLISTITEM (state, val) {
      state.currentRux.packingListItems.splice(val, 1)
    },
    // Deletes the packing list
    DELETEALLPACKINGLISTITEMS (state) {
      state.currentRux.packingListItems = []
    },
    UPDATEPACKINGLIST (state, val) {
      const { index, value } = val
      state.currentRux.packingListItems[index].checked = value
    },
    SEARCHBAR (state, val) {
      state.showSearchBar = val
    },
    SETGEOLOCATION (state, val) {
      const { lat, long, isSet } = val
      state.geolocation.latitude = lat
      state.geolocation.longitude = long
      state.geolocation.isSet = isSet
    },
    SETSERIALNUMBERFORREGISTRATION (state, val) {
      state.register.serialNumber = val
    },
    SETFORCEPINONREGISTRATION (state, val) {
      state.register.forcePin = val
    },
    SETSHOWLOADING (state, val) {
      state.showLoading = val
    },
    CLEARCURRENTRUX (state) {
      state.currentRux = {}
      state.currentRuxImages = []
    },
    // Add the Errors here
    ERROR (state, val) {
      console.log(state, val)
      state.errors = [val, ...state.errors]
    },
    // Add the Succes here
    SUCCESS (state, val) {
      state.successes = [val, ...state.successes]
    }

  },
  getters: {
    // Gets the details for the rux by serial number
    getRuxDetails: (state) => (serialNumber) => {
      return state.ruxList.find((rux) => {
        return rux.serialNumber === serialNumber
      })
    },
    // Gets the number of RUX in the state.
    getRuxListLength: (state) => {
      if (Array.isArray(state.ruxList)) {
        return state.ruxList.length
      } else {
        return 0
      }
    },
    // This gets the user profile. Litte redundant.
    getUserProfile: (state) => {
      return state.userProfile
    }
  }
}

export { RuxStore }
